type RecordingState = 'active' | 'paused' | 'dormant';

export interface RecordingCallbacks {
  /** Hidden past the grace window: stop heavy recording, session stays alive. */
  pause(): void;
  /** Visible again after a hidden pause: restart recording, same session. */
  resume(): void;
  /** Idle past the window: end + seal the session and stop everything. */
  endAndSuspend(): void;
  /** First interaction after idle: start a fresh session (new id + snapshot). */
  wake(): void;
  /** Interaction while active: keep the current session/visit alive. */
  activity(): void;
}

/**
 * Per-tab recording/session lifecycle:
 *   active  --hidden > graceMs-->      paused   (rrweb off, session alive)
 *   paused  --visible-->               active   (resume same session)
 *   active/paused --idle > idleMs-->   dormant  (session sealed, all off)
 *   dormant --interaction-->           active   (rotate to a NEW session)
 *
 * Pure of the DOM: the host feeds interaction/visibility and provides callbacks.
 */
export class RecordingController {
  private state: RecordingState = 'active';
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly idleMs: number,
    private readonly hiddenGraceMs: number,
    private readonly cb: RecordingCallbacks
  ) {
    this.armIdle();
  }

  notifyInteraction(): void {
    if (this.state === 'dormant') {
      this.state = 'active';
      this.cb.wake();
    } else if (this.state === 'paused') {
      this.state = 'active';
      this.cb.resume();
      this.cb.activity();
    } else {
      this.cb.activity();
    }
    this.armIdle();
  }

  notifyVisibility(hidden: boolean): void {
    if (hidden) {
      if (this.state !== 'active' || this.hiddenTimer) return;
      this.hiddenTimer = setTimeout(() => {
        this.hiddenTimer = null;
        if (this.state === 'active') {
          this.state = 'paused';
          this.cb.pause();
        }
      }, this.hiddenGraceMs);
      return;
    }
    this.clearHidden();
    if (this.state === 'paused') {
      this.state = 'active';
      this.cb.resume();
    }
  }

  private armIdle(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (this.state !== 'dormant') {
        this.state = 'dormant';
        this.clearHidden();
        this.cb.endAndSuspend();
      }
    }, this.idleMs);
  }

  private clearHidden(): void {
    if (this.hiddenTimer) {
      clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
    }
  }

  dispose(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.clearHidden();
  }
}
